import { useRef, useLayoutEffect, useState, useEffect } from "react";
import { getPoiSceneDefByLabel } from '../../../../config/poiSceneConfig';

interface PointPopupProps {
  onClose: () => void;
  poidata?: any | null;
  visible: boolean;
  followRef?: React.RefObject<{ x: number; y: number } | null>;
}

const PointPopup: React.FC<PointPopupProps> = ({
  onClose,
  poidata,
  visible,
  followRef
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({
    position: "absolute",
    zIndex: 2000,
    width: 288,
    opacity: 0,
    pointerEvents: "none",
  });

  // 根据类别获取对应的颜色类名
  const getCategoryColor = (category: string): string => {
    const def = getPoiSceneDefByLabel(category);
    return def.bg;
  };

  // 仅保留 POI点 的配置
  const layerConfig = {
    title: poidata?.name || '未知POI',
    subtitle: 'POI点',
    icon: 'ri-map-pin-line',
    iconColor: getCategoryColor(poidata?.poiScene || poidata?.poi_scene || '其他')
  };

  // 仅生成 POI点 的数据行
  const generateDataRows = () => {
    return [
      { label: 'ID', value: poidata?.wyid || '未知', icon: 'ri-key-line' },
      { label: '类别', value: poidata?.poiScene || poidata?.poi_scene || '未知', icon: 'ri-bookmark-line' },
      { label: '地址', value: poidata?.addr || '未知', icon: 'ri-map-pin-2-line' },
      { label: '电话', value: poidata?.phone || '未知', icon: 'ri-phone-line' },
      { label: '营业时间', value: poidata?.businessHours || '未知', icon: 'ri-time-line' }
    ];
  };

  // 初始定位 + 实时跟随
  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const cardHeight = cardRef.current.offsetHeight || 480;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const TOP_NAV_H = 56;
    const GAP = 24;
    const CARD_WIDTH = 288;

    // 计算并更新位置的函数（可复用）
    const applyPosition = (pos: { x: number; y: number }) => {
      let left = pos.x + GAP;
      if (left + CARD_WIDTH > vpW - GAP) {
        left = pos.x - CARD_WIDTH - GAP;
      }
      let top = pos.y - cardHeight / 2;
      left = Math.max(GAP, Math.min(vpW - CARD_WIDTH - GAP, left));
      top = Math.max(TOP_NAV_H + GAP, Math.min(vpH - cardHeight - GAP, top));
      if (cardRef.current) {
        cardRef.current.style.left = left + 'px';
        cardRef.current.style.top = top + 'px';
      }
    };

    // 1. 初始位置（从 poidata.clickPos）
    if (poidata?.clickPos) {
      applyPosition(poidata.clickPos);
    } else {
      let left = vpW - CARD_WIDTH - GAP * 2;
      let top = vpH / 2 - cardHeight / 2;
      left = Math.max(GAP, Math.min(vpW - CARD_WIDTH - GAP, left));
      top = Math.max(TOP_NAV_H + GAP, Math.min(vpH - cardHeight - GAP, top));
      if (cardRef.current) {
        cardRef.current.style.left = left + 'px';
        cardRef.current.style.top = top + 'px';
      }
    }

    // 显示
    setCardStyle({
      position: "absolute",
      zIndex: 2000,
      width: CARD_WIDTH,
      opacity: 1,
      pointerEvents: "auto",
      transition: "opacity 0.15s ease",
    });

    // 2. 每帧实时跟随（通过 followRef 接收最新屏幕坐标）
    let rafId: number;
    const follow = () => {
      if (followRef?.current) {
        applyPosition(followRef.current);
      }
      rafId = requestAnimationFrame(follow);
    };
    rafId = requestAnimationFrame(follow);

    return () => cancelAnimationFrame(rafId);
  }, [poidata, followRef]);

  if (!visible) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      style={{ ...cardStyle, boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${layerConfig.iconColor} text-white`}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`${layerConfig.icon} text-base`}></i>
          </div>
          <div>
            <p className="text-xs text-white/70 leading-tight">{layerConfig.subtitle}</p>
            <h3 className="text-sm font-semibold leading-tight truncate max-w-[180px]">{layerConfig.title}</h3>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {/* Info rows */}
      <div className="max-h-64 overflow-y-auto">
        {generateDataRows().map((row, idx) => (
          <div
            key={row.label}
            className={`flex items-start px-4 py-2 ${idx % 2 === 0 ? "bg-slate-50" : "bg-white"}`}
          >
            {row.icon && (
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 mr-1.5">
                <i className={`${row.icon} text-slate-400 text-xs`}></i>
              </div>
            )}
            <span className="text-xs text-slate-500 w-16 flex-shrink-0 pt-0.5">{row.label}</span>
            <span className="text-xs text-slate-800 font-medium flex-1 leading-relaxed">{row.value}</span>
          </div>
        ))}
      </div>

      {/* Locked hint */}
      <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-lock-line text-amber-500 text-xs"></i>
        </div>
        <span className="text-xs text-amber-700">更多数据字段需升级后查看</span>
      </div>

      {/* Export button */}
      <div className="px-4 py-3 border-t border-slate-100 bg-white">
        <button
          className={`w-full py-2 rounded-lg ${layerConfig.iconColor} hover:opacity-90 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-download-line text-base"></i>
          </div>
          一键导出数据
        </button>
      </div>
    </div>
  );
};

export default PointPopup;